import React, { useEffect, useState } from 'react';
import { apiJson } from '../../../components/apiClient';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Panel } from '../ui/Panel';
import { Sheet } from '../ui/Sheet';
import { Tabs } from '../ui/Tabs';

const apiFin = (path, opts) => apiJson(`/financeiro${path}`, opts);

const fmtBRL = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return typeof n === 'number' && !isNaN(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');

const EVENTOS_GATILHO = [
  { evento: 'ANTES_VENCIMENTO', dias: 5, label: 'D-5' }, { evento: 'ANTES_VENCIMENTO', dias: 2, label: 'D-2' },
  { evento: 'NO_VENCIMENTO', dias: 0, label: 'D0' }, { evento: 'APOS_VENCIMENTO', dias: 1, label: 'D+1' },
  { evento: 'APOS_VENCIMENTO', dias: 2, label: 'D+2' }, { evento: 'APOS_VENCIMENTO', dias: 15, label: 'D+15' },
];
const VARIAVEIS = ['{{nome}}', '{{cliente}}', '{{produto}}', '{{plano}}', '{{valor}}', '{{vencimento}}', '{{link_pagamento}}'];
const rotuloGatilho = (g) => (g.evento === 'ANTES_VENCIMENTO' ? `D-${g.dias}` : g.evento === 'APOS_VENCIMENTO' ? `D+${g.dias}` : 'D0');

// Wizard de cobrança automática (§22 do redesign) — componente compartilhado
// (extraído de Financeiro.jsx pra ser reaproveitado também dentro de
// AssinaturaSheet, mesmo padrão de extração já usado pra ela própria).
export function CobrancaWizardSheet({ receivableIdInicial, onClose, onSalvo, showToast }) {
  const [passo, setPasso] = useState(receivableIdInicial ? 'regua' : 'dados');
  const [receivables, setReceivables] = useState([]);
  const [receivableId, setReceivableId] = useState(receivableIdInicial || '');
  const [gatilhos, setGatilhos] = useState(EVENTOS_GATILHO.map((g) => ({ ...g, ativo: true, hora: '08:00', mensagem: `Olá {{nome}}! Sua cobrança *{{produto}}* vence em *{{vencimento}}*. Valor: *{{valor}}*` })));
  const [gatilhoEditando, setGatilhoEditando] = useState(0);
  const [preview, setPreview] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { apiFin('/receivables').then((r) => setReceivables((r.lista || []).filter((x) => x.situacao !== 'PAGO' && x.situacao !== 'CANCELADO'))); }, []);

  useEffect(() => {
    if (!receivableIdInicial) return;
    apiFin(`/receivables/${receivableIdInicial}`).then((r) => {
      if (r.status === 'ok' && r.receivable?.billing_automation) setGatilhos(r.receivable.billing_automation.gatilhos);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (passo !== 'mensagens') return;
    apiFin('/waha/preview_template', { method: 'POST', body: JSON.stringify({ template: gatilhos[gatilhoEditando]?.mensagem || '' }) }).then((r) => setPreview(r.preview || ''));
  }, [passo, gatilhoEditando, gatilhos]);

  const atualizarGatilho = (i, campo, valor) => setGatilhos(gatilhos.map((g, idx) => (idx === i ? { ...g, [campo]: valor } : g)));

  const salvar = async (ativar) => {
    if (!receivableId) { showToast('Selecione a cobrança', 'error'); return; }
    setSalvando(true);
    const r = await apiFin(`/receivables/${receivableId}/billing-automation`, { method: 'PUT', body: JSON.stringify({ ativa: ativar, gatilhos }) });
    setSalvando(false);
    if (r.status === 'ok') { showToast(ativar ? 'Régua salva e ativada!' : 'Rascunho salvo.', 'success'); onSalvo(); }
    else showToast(r.mensagem || 'Erro ao salvar', 'error');
  };

  const PASSOS = [{ id: 'dados', label: 'Dados' }, { id: 'regua', label: 'Régua' }, { id: 'mensagens', label: 'Mensagens' }, { id: 'revisar', label: 'Revisar' }];
  const receivableAtual = receivables.find((r) => r.id === receivableId);

  return (
    <Sheet open onClose={onClose} title="Cobrança automática">
      <Tabs tabs={PASSOS} atual={passo} onChange={setPasso} />
      <div style={{ marginTop: 'var(--crm-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-3)' }}>
        {passo === 'dados' && (
          <div className="crm-form-field">
            <label className="crm-form-label">Cobrança (conta a receber)</label>
            <select className="crm-select" value={receivableId} onChange={(e) => setReceivableId(e.target.value)}>
              <option value="">Selecione...</option>
              {receivables.map((r) => <option key={r.id} value={r.id}>{r.descricao} — {r.cliente_nome || 'sem cliente'} — {fmtBRL(r.valor)}</option>)}
            </select>
          </div>
        )}

        {passo === 'regua' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)' }}>
            {gatilhos.map((g, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', padding: 'var(--crm-space-2)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)' }}>
                <input type="checkbox" checked={g.ativo} onChange={(e) => atualizarGatilho(i, 'ativo', e.target.checked)} />
                <Badge tone="accent">{rotuloGatilho(g)}</Badge>
                <input className="crm-input" type="time" value={g.hora} onChange={(e) => atualizarGatilho(i, 'hora', e.target.value)} style={{ maxWidth: '110px' }} />
              </div>
            ))}
          </div>
        )}

        {passo === 'mensagens' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-3)' }}>
            <div className="crm-form-field">
              <label className="crm-form-label">Etapa</label>
              <select className="crm-select" value={gatilhoEditando} onChange={(e) => setGatilhoEditando(Number(e.target.value))}>
                {gatilhos.map((g, i) => <option key={i} value={i}>{rotuloGatilho(g)}</option>)}
              </select>
            </div>
            <div className="crm-form-field">
              <label className="crm-form-label">Mensagem</label>
              <textarea className="crm-input" rows={5} value={gatilhos[gatilhoEditando]?.mensagem || ''} onChange={(e) => atualizarGatilho(gatilhoEditando, 'mensagem', e.target.value)} />
            </div>
            <div className="crm-form-hint">Variáveis: {VARIAVEIS.join(' ')}</div>
            <Panel title="Preview">{preview || '—'}</Panel>
          </div>
        )}

        {passo === 'revisar' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)' }}>
            <div><strong>Cliente:</strong> {receivableAtual?.cliente_nome || '—'}</div>
            <div><strong>Produto:</strong> {receivableAtual?.descricao || '—'}</div>
            <div><strong>Valor:</strong> {receivableAtual ? fmtBRL(receivableAtual.valor) : '—'}</div>
            <div><strong>Vencimento:</strong> {receivableAtual ? fmtDate(receivableAtual.vencimento) : '—'}</div>
            <div><strong>Etapas ativas:</strong> {gatilhos.filter((g) => g.ativo).map(rotuloGatilho).join(', ') || 'nenhuma'}</div>
            <div className="crm-badge crm-badge-warning" style={{ marginTop: 'var(--crm-space-2)', alignSelf: 'flex-start' }}>Modo de teste (Dry Run) ativo se WhatsApp não estiver conectado — mensagens não serão enviadas de verdade</div>
            <div style={{ display: 'flex', gap: 'var(--crm-space-2)', marginTop: 'var(--crm-space-3)' }}>
              <Button variant="primary" loading={salvando} onClick={() => salvar(true)}>Salvar e ativar</Button>
              <Button variant="outline" loading={salvando} onClick={() => salvar(false)}>Salvar como rascunho</Button>
              <Button variant="ghost" onClick={onClose}>Cancelar</Button>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  );
}

export default CobrancaWizardSheet;
